import { ref } from 'vue'
import { useApi } from '@/composables/useApi'
import type { ApiResponse } from '@/types/api'
import type {
  ResultatsClasseDisponibilite,
  ResultatsClassePayload,
} from '@/types/resultatsClasse'
import { formatErrorMessage } from '@/utils/errorHandler'

export function useResultatsClasse() {
  const { apiData } = useApi()
  const loading = ref(false)

  async function getDisponibilite(sessionId: number, specialiteId: number) {
    loading.value = true
    try {
      const qs = new URLSearchParams({ specialite_id: String(specialiteId) })
      const res = await apiData<ApiResponse<ResultatsClasseDisponibilite>>(
        `/sessions/${sessionId}/resultats-classe/disponibilite?${qs.toString()}`
      )
      if (res?.success && res.data) {
        return { success: true as const, data: res.data }
      }
      return { success: false as const, message: res?.message || 'Disponibilité indisponible' }
    } catch (error: any) {
      return {
        success: false as const,
        message: formatErrorMessage(
          'Erreur lors de la vérification de disponibilité',
          error?.response?._data || error,
          { action: 'Vérification', entity: 'résultats de classe' }
        ),
      }
    } finally {
      loading.value = false
    }
  }

  async function getResultatsClasse(sessionId: number, specialiteId: number) {
    loading.value = true
    try {
      const qs = new URLSearchParams({ specialite_id: String(specialiteId) })
      const res = await apiData<ApiResponse<ResultatsClassePayload>>(
        `/sessions/${sessionId}/resultats-classe?${qs.toString()}`
      )
      if (res?.success && res.data) {
        return { success: true as const, data: res.data }
      }
      return {
        success: false as const,
        message: res?.message || 'Document indisponible',
        code: (res as any)?.data?.code,
        disponibilite: (res as any)?.data?.disponibilite as ResultatsClasseDisponibilite | undefined,
      }
    } catch (error: any) {
      const payload = error?.response?._data || error?.data || error
      return {
        success: false as const,
        message: formatErrorMessage(
          'Erreur lors du chargement des résultats de classe',
          payload,
          { action: 'Chargement', entity: 'résultats de classe' }
        ),
        code: payload?.data?.code,
        disponibilite: payload?.data?.disponibilite as ResultatsClasseDisponibilite | undefined,
      }
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    getDisponibilite,
    getResultatsClasse,
  }
}
